import { Button } from "@/components/Button";
import { fetchLeaseTasks, leaseTasksQueryKey } from "@/components/Tasks";
import Layout from "@/components/Layout";
import { useRouteParams } from "@/hooks/useEncodedIds";
import { useQueryClient } from "@tanstack/react-query";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { motion } from "framer-motion";
import Confetti from "react-confetti";
import { useState } from "react";
import { useNavigate } from "react-router";

export default function SurveyThankYouPage() {
  const params = useRouteParams<{ leaseId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showConfetti, setShowConfetti] = useState(true);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const leaseId = Number(params.leaseId);

  const handleDone = async () => {
    if (!Number.isFinite(leaseId) || isRedirecting) {
      return;
    }

    setIsRedirecting(true);

    try {
      await queryClient.invalidateQueries({
        queryKey: leaseTasksQueryKey(leaseId),
        refetchType: "none",
      });

      await queryClient.fetchQuery({
        queryKey: leaseTasksQueryKey(leaseId),
        queryFn: () => fetchLeaseTasks(leaseId),
      });

      navigate(`/lease/${leaseId}/home`);
    } catch {
      setIsRedirecting(false);
    }
  };

  return (
    <Layout className="min-h-screen bg-[#FFFDFC]" title="Thank You">
      {showConfetti && (
        <Confetti
          recycle={false}
          numberOfPieces={220}
          onConfettiComplete={() => setShowConfetti(false)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            zIndex: 30,
            pointerEvents: "none",
          }}
          colors={["#4EC779", "#F9877E", "#92D1FF"]}
        />
      )}

      <div className="relative z-40 flex min-h-[78vh] flex-col items-center justify-center px-4 text-center">
        <motion.div
          initial={{ scale: 0.6, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 220, damping: 16 }}
          className="flex h-20 w-20 items-center justify-center rounded-full bg-[#4EC779]"
        >
          <motion.div
            initial={{ scale: 0.7, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.3 }}
          >
            <FontAwesomeIcon icon="check" className="text-4xl text-white" />
          </motion.div>
        </motion.div>

        <h1 className="mt-8 text-3xl font-semibold text-gray-800 md:text-4xl">
          Thank you for your feedback!
        </h1>
        <p className="mt-3 max-w-md text-base text-gray-500">
          Your responses help us improve your living experience.
        </p>

        <div className="mt-12 w-full max-w-md">
          <Button
            color="secondary"
            className="h-12 w-full text-base font-semibold"
            disabled={isRedirecting}
            onClick={handleDone}
          >
            {isRedirecting ? "Loading..." : "Done"}
          </Button>
        </div>
      </div>
    </Layout>
  );
}
